import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import type { ComponentProps, HTMLAttributes, Ref } from 'react';

import { buttonVariants } from './button';

import { cn } from '@/lib/cn';

export const AlertDialog = AlertDialogPrimitive.Root;
export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;
export const AlertDialogPortal = AlertDialogPrimitive.Portal;

export const AlertDialogOverlay = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Overlay> & {
  ref?: Ref<HTMLDivElement>;
}) => (
  <AlertDialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
);
AlertDialogOverlay.displayName = 'AlertDialogOverlay';

export const AlertDialogContent = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Content> & {
  ref?: Ref<HTMLDivElement>;
}) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border border-border bg-background p-6 shadow-lg sm:rounded-lg',
        className,
      )}
      {...props}
    />
  </AlertDialogPortal>
);
AlertDialogContent.displayName = 'AlertDialogContent';

export const AlertDialogHeader = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />
);
AlertDialogHeader.displayName = 'AlertDialogHeader';

export const AlertDialogFooter = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
    {...props}
  />
);
AlertDialogFooter.displayName = 'AlertDialogFooter';

export const AlertDialogTitle = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Title> & {
  ref?: Ref<HTMLHeadingElement>;
}) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold', className)}
    {...props}
  />
);
AlertDialogTitle.displayName = 'AlertDialogTitle';

export const AlertDialogDescription = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Description> & {
  ref?: Ref<HTMLParagraphElement>;
}) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
);
AlertDialogDescription.displayName = 'AlertDialogDescription';

export const AlertDialogAction = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Action> & {
  ref?: Ref<HTMLButtonElement>;
}) => (
  <AlertDialogPrimitive.Action ref={ref} className={cn(buttonVariants(), className)} {...props} />
);
AlertDialogAction.displayName = 'AlertDialogAction';

export const AlertDialogCancel = ({
  className,
  ref,
  ...props
}: ComponentProps<typeof AlertDialogPrimitive.Cancel> & {
  ref?: Ref<HTMLButtonElement>;
}) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(buttonVariants({ variant: 'outline' }), 'mt-2 sm:mt-0', className)}
    {...props}
  />
);
AlertDialogCancel.displayName = 'AlertDialogCancel';
